/*
 * B2B reverse charge: the VAT number form (snippets/b2b-vat.liquid) on the cart, the corporate PDP and the B2B page.
 *
 * Asks api.cityframes.de through the App Proxy (/apps/cube/b2b/verify; printable-city-models
 * pipeline/b2b_vat.py) and shows its answer. Every text comes translated from the snippet's JSON.
 * Event delegation, because the cart section is re-rendered whole when a quantity changes.
 *
 * Answers: verified | mismatch (fields) | pending | invalid | wrong_country | too_many |
 * unavailable | signin | incomplete.
 */
(() => {
  if (window.cfB2bVat) return;
  window.cfB2bVat = true;

  // The legal form the "details don't match" message suggests, by the VAT number's country.
  const LEGAL_FORM = {
    AT: 'GmbH', BE: 'SRL', BG: 'EOOD', CY: 'Ltd', CZ: 's.r.o.', DK: 'ApS', EE: 'OÜ', EL: 'IKE', ES: 'S.L.',
    FI: 'Oy', FR: 'SARL', HR: 'd.o.o.', HU: 'Kft.', IE: 'Ltd', IT: 'S.r.l.', LT: 'UAB', LU: 'S.à r.l.',
    LV: 'SIA', MT: 'Ltd', NL: 'B.V.', PL: 'sp. z o.o.', PT: 'Lda.', RO: 'S.R.L.', SE: 'AB', SI: 'd.o.o.', SK: 's.r.o.',
  };
  const FLASH = 'cf-b2b-verified';

  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const strings = (root) => {
    try { return JSON.parse(root.querySelector('[data-b2b-vat-strings]').textContent); } catch { return {}; }
  };

  // Right after a pass the page reloads (so the cart is priced again); say once that it passed.
  const flash = () => {
    let html = null;
    try { html = sessionStorage.getItem(FLASH); sessionStorage.removeItem(FLASH); } catch { /* private mode */ }
    if (!html) return;
    document.querySelectorAll('[data-b2b-vat-flash]').forEach((el) => { el.innerHTML = html; el.hidden = false; });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', flash); else flash();

  // Before the page is left for signing in, or reloaded after a pass: a page that keeps choices in its address writes
  // them there first (the corporate PDP, assets/corporate-kit.js), so the buyer comes back to what they had set up.
  const keepState = () => document.dispatchEvent(new CustomEvent('cf:keep-state'));

  document.addEventListener('click', (e) => {
    const login = e.target.closest('[data-b2b-vat] a[href*="return_to="]');
    if (login) {
      keepState();
      const url = new URL(login.getAttribute('href'), window.location.href);
      url.searchParams.set('return_to', window.location.pathname + window.location.search);
      login.href = url.toString();
      return;
    }
    const open = e.target.closest('[data-b2b-vat-open]');
    if (open) {
      const form = document.getElementById(open.getAttribute('aria-controls'));
      if (!form) return;
      form.hidden = false;
      open.setAttribute('aria-expanded', 'true');
      form.querySelector('[data-field="vat"]')?.focus();
      return;
    }
    const button = e.target.closest('[data-b2b-vat-form] .button');
    if (button) { e.preventDefault(); verify(button.closest('[data-b2b-vat-form]')); }
  });

  // The cart page is one form: Enter in a field must check the number, not start checkout.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    const form = e.target.closest?.('[data-b2b-vat-form]');
    if (!form || !e.target.matches('input')) return;
    e.preventDefault();
    verify(form);
  });

  async function verify(form) {
    const root = form.closest('[data-b2b-vat]');
    const S = strings(root);
    const msg = form.querySelector('[data-b2b-vat-msg]');
    const button = form.querySelector('.button');
    const fields = {};
    form.querySelectorAll('[data-field]').forEach((input) => { fields[input.dataset.field] = input; input.removeAttribute('aria-invalid'); });
    const say = (html, error) => {
      msg.innerHTML = html || '';
      msg.hidden = !html;
      msg.classList.toggle('text-error', !!error);
    };

    const empty = Object.values(fields).filter((input) => !input.value.trim());
    if (empty.length) {
      empty.forEach((input) => input.setAttribute('aria-invalid', 'true'));
      empty[0].focus();
      say('');
      return;
    }
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    say('');

    let res = {};
    try {
      const r = await fetch('/apps/cube/b2b/verify', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          vat: fields.vat.value, company: fields.company.value, street: fields.street.value,
          zip: fields.zip.value, city: fields.city.value, country: root.dataset.country,
          source: root.dataset.placement,
        }),
      });
      res = await r.json().catch(() => ({}));
    } catch {
      res = { result: 'unavailable' };
    }
    button.disabled = false;
    button.removeAttribute('aria-busy');

    switch (res.result) {
      case 'verified': {
        const html = S.verified.replace('%COMPANY%', esc(res.company)).replace('%VAT%', esc(res.vat));
        say(html);
        try { sessionStorage.setItem(FLASH, html); } catch { /* private mode */ }
        // Any change to the cart makes Shopify price it again, now for a checked business.
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { _b2b_checked: new Date().toISOString() } }),
          });
        } catch { /* the reload still shows the new state */ }
        keepState();
        window.location.reload();
        return;
      }
      case 'mismatch': {
        const names = (res.fields || []).map((f) => S.fields?.[f]).filter(Boolean).join(', ');
        (res.fields || []).forEach((f) => fields[f]?.setAttribute('aria-invalid', 'true'));
        const example = LEGAL_FORM[String(res.vat || '').slice(0, 2)] || LEGAL_FORM[root.dataset.country] || 'SARL';
        say(S.mismatch.replace('%FIELDS%', esc(names)).replace('%EXAMPLE%', esc(example)), true);
        return;
      }
      case 'pending':
        { const cls = root.dataset.placement === 'page' ? '' : 'text-xs text-subdued'; root.querySelector('[data-b2b-vat-body]').innerHTML = `<p class="${cls}">${S.pending}</p>`; }
        return;
      case 'invalid':
        fields.vat.setAttribute('aria-invalid', 'true');
        say(S.invalid, true);
        return;
      case 'wrong_country':
        fields.vat.setAttribute('aria-invalid', 'true');
        say(S.wrong_country, true);
        return;
      case 'too_many':
        say(S.too_many, true);
        return;
      case 'signin':
        say(S.signed_out, true);
        return;
      default:
        say(S.unavailable, true);
    }
  }
})();
